import { auth } from '@/utils/firebaseConfig'
import { logger } from '@/utils/logger'

// トークンキャッシュ用の定数
const TOKEN_CACHE_KEY = 'firebase_token_cache'
const TOKEN_REFRESH_LOCK = 'token_refresh_lock'
const LOCK_TIMEOUT = 5000 // 5秒でロックタイムアウト

/**
 * Firebase認証状態が準備完了するまで待機する関数
 * @param timeoutMs タイムアウト時間（ミリ秒）
 * @returns 認証状態が準備できたかどうか
 */
export async function waitForAuth(timeoutMs: number = 5000): Promise<boolean> {
  logger.debug('認証状態待機開始', { timeout: timeoutMs })

  return new Promise(resolve => {
    const timeout = setTimeout(() => {
      logger.warn('認証状態待機タイムアウト', { timeoutMs })
      resolve(false)
    }, timeoutMs)

    const unsubscribe = auth.onAuthStateChanged(user => {
      // For new tabs, accept any auth state change (including null)
      // This ensures we don't wait indefinitely for a user that might not exist
      logger.debug('認証状態確認完了', {
        userEmail: user?.email,
        hasUid: !!user?.uid,
        isLoggedIn: !!user,
      })
      clearTimeout(timeout)
      unsubscribe()
      resolve(!!user)
    })

    // 既に認証済みの場合
    if (auth.currentUser) {
      logger.debug('既存認証状態確認', {
        userEmail: auth.currentUser.email,
        hasUid: !!auth.currentUser.uid,
      })
      clearTimeout(timeout)
      unsubscribe()
      resolve(true)
    }
  })
}

/**
 * 現在の認証状態をチェックする関数
 * @returns 認証されているかどうか
 */
export function isAuthenticated(): boolean {
  const authenticated = !!(auth.currentUser && auth.currentUser.uid)
  logger.debug('認証状態チェック', {
    hasCurrentUser: !!auth.currentUser,
    hasUid: !!auth.currentUser?.uid,
    authenticated,
  })
  return authenticated
}

/**
 * Firebaseユーザーの詳細情報を取得する関数
 * @returns ユーザー情報またはnull
 */
export function getCurrentUserInfo() {
  if (!auth.currentUser) {
    return null
  }

  return {
    uid: auth.currentUser.uid,
    email: auth.currentUser.email,
    emailVerified: auth.currentUser.emailVerified,
    displayName: auth.currentUser.displayName,
  }
}

/**
 * 認証状態が利用可能になるまでリトライを伴って待機する関数
 * @param maxRetries 最大リトライ回数
 * @param retryDelay リトライ間隔（ミリ秒）
 * @returns 認証されているかどうか
 */
export async function waitForAuthWithRetry(
  maxRetries: number = 3,
  retryDelay: number = 1000
): Promise<boolean> {
  for (let i = 0; i < maxRetries; i++) {
    logger.debug(`認証状態確認試行 ${i + 1}/${maxRetries}`)

    // First check if already authenticated
    if (isAuthenticated()) {
      logger.debug('認証済み状態を確認')
      return true
    }

    // Wait for auth state
    const authReady = await waitForAuth(2000)
    if (authReady) {
      logger.debug(`認証状態確立 (試行 ${i + 1})`)
      return true
    }

    // Wait before retry
    if (i < maxRetries - 1) {
      logger.debug(`${retryDelay}ms待機してリトライ`)
      await new Promise(resolve => setTimeout(resolve, retryDelay))
    }
  }

  logger.warn('認証状態の確立に失敗（最大リトライ回数到達）')
  return false
}

/**
 * トークンキャッシュからトークンを取得
 */
function getCachedToken(): { token: string; expiry: number } | null {
  try {
    const cached = localStorage.getItem(TOKEN_CACHE_KEY)
    if (cached) {
      return JSON.parse(cached)
    }
  } catch (error) {
    logger.warn('Failed to parse cached token:', error)
    localStorage.removeItem(TOKEN_CACHE_KEY)
  }
  return null
}

/**
 * トークンをキャッシュに保存
 */
function setCachedToken(token: string, expiry: number): void {
  try {
    localStorage.setItem(
      TOKEN_CACHE_KEY,
      JSON.stringify({
        token,
        expiry,
        timestamp: Date.now(),
      })
    )
  } catch (error) {
    logger.warn('Failed to cache token:', error)
  }
}

/**
 * トークンキャッシュをクリア
 */
export function clearTokenCache(): void {
  localStorage.removeItem(TOKEN_CACHE_KEY)
  localStorage.removeItem(TOKEN_REFRESH_LOCK)
  logger.debug('Token cache cleared')
}

/**
 * 排他的なトークンリフレッシュロックを取得
 */
async function acquireRefreshLock(): Promise<string | null> {
  const lockId = `${Date.now()}_${Math.random()}`

  try {
    // 既存のロックをチェック
    const existingLock = localStorage.getItem(TOKEN_REFRESH_LOCK)
    if (existingLock) {
      const lockData = JSON.parse(existingLock)
      const lockAge = Date.now() - lockData.timestamp

      // ロックがタイムアウトしていない場合は待機
      if (lockAge < LOCK_TIMEOUT) {
        logger.debug('Token refresh lock exists, waiting...', { lockAge, lockData })
        return null
      }

      // タイムアウトしたロックは削除
      logger.debug('Removing timed out lock', { lockAge, lockData })
    }

    // 新しいロックを設定
    localStorage.setItem(
      TOKEN_REFRESH_LOCK,
      JSON.stringify({
        lockId,
        timestamp: Date.now(),
      })
    )

    // ロックの競合チェック（100ms待機）
    await new Promise(resolve => setTimeout(resolve, 100))

    const currentLock = localStorage.getItem(TOKEN_REFRESH_LOCK)
    if (currentLock) {
      const lockData = JSON.parse(currentLock)
      if (lockData.lockId === lockId) {
        logger.debug('Token refresh lock acquired', { lockId })
        return lockId
      }
    }

    logger.debug('Failed to acquire token refresh lock', { lockId })
    return null
  } catch (error) {
    logger.error('Error acquiring refresh lock:', error)
    return null
  }
}

/**
 * トークンリフレッシュロックを解放
 */
function releaseRefreshLock(lockId: string): void {
  try {
    const currentLock = localStorage.getItem(TOKEN_REFRESH_LOCK)
    if (currentLock) {
      const lockData = JSON.parse(currentLock)
      if (lockData.lockId === lockId) {
        localStorage.removeItem(TOKEN_REFRESH_LOCK)
        logger.debug('Token refresh lock released', { lockId })
      }
    }
  } catch (error) {
    logger.error('Error releasing refresh lock:', error)
  }
}

/**
 * 認証トークンを安全に取得する関数（インテリジェントなリフレッシュ戦略）
 * @param forceRefresh トークンを強制更新するかどうか
 * @returns 認証トークンまたはnull
 */
export async function getSafeAuthToken(forceRefresh: boolean = false): Promise<string | null> {
  try {
    if (!auth.currentUser) {
      logger.warn('認証トークン取得失敗: currentUserが存在しません')
      return null
    }

    // 強制更新が指定されていない場合は、まずキャッシュを確認
    if (!forceRefresh) {
      const cached = getCachedToken()
      if (cached) {
        // キャッシュの有効期限をチェック（10分前まで有効）
        const bufferTime = 10 * 60 * 1000 // 10分
        if (Date.now() < cached.expiry - bufferTime) {
          logger.debug('キャッシュされた有効なトークンを使用', {
            tokenLength: cached.token.length,
            remainingTime: cached.expiry - Date.now(),
          })
          return cached.token
        } else {
          logger.debug('キャッシュされたトークンが期限切れ間近', {
            remainingTime: cached.expiry - Date.now(),
          })
        }
      }
    }

    // トークンリフレッシュが必要な場合は排他制御
    let lockId: string | null = null
    let retryCount = 0
    const maxRetries = 5

    while (retryCount < maxRetries) {
      lockId = await acquireRefreshLock()

      if (lockId) {
        // ロックを取得できた場合はトークンをリフレッシュ
        try {
          const token = await auth.currentUser.getIdToken(true)
          if (token) {
            // トークンの有効期限を取得してキャッシュ
            const payload = JSON.parse(atob(token.split('.')[1]))
            const expiry = payload.exp * 1000
            setCachedToken(token, expiry)

            logger.debug('認証トークンリフレッシュ成功', {
              tokenLength: token.length,
              expiry: new Date(expiry).toISOString(),
              forceRefresh,
            })

            return token
          }
        } finally {
          // ロックを解放
          releaseRefreshLock(lockId)
        }
      } else {
        // ロックを取得できなかった場合は、他のタブがリフレッシュ中
        logger.debug('他のタブがトークンをリフレッシュ中、待機します', {
          retry: retryCount + 1,
        })

        // 少し待機してからキャッシュを再確認
        await new Promise(resolve => setTimeout(resolve, 300))

        // キャッシュが更新されているか確認
        const cached = getCachedToken()
        if (cached && Date.now() < cached.expiry - 5 * 60 * 1000) {
          logger.debug('他のタブがリフレッシュしたトークンを使用', {
            tokenLength: cached.token.length,
          })
          return cached.token
        }
      }

      retryCount++
    }

    // すべてのリトライが失敗
    logger.error('トークンリフレッシュの排他制御に失敗', { maxRetries })

    // フォールバック: 直接トークンを取得
    const token = await auth.currentUser.getIdToken(true)
    return token
  } catch (error) {
    logger.error('認証トークン取得エラー:', {
      error: error instanceof Error ? error.message : String(error),
      forceRefresh,
      hasCurrentUser: !!auth.currentUser,
    })
    return null
  }
}
